import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { X, Sparkles, ArrowDown, ArrowUp, Minus } from "lucide-react";

/**
 * Iter 98 — SHAP-style "Why" panel
 *
 * Modal that explains exactly how a market's confidence number was
 * arrived at: base model probability → calibration shrink → posterior
 * shrink → lineup fade → precision tuning → final.
 *
 * Adjustments are parsed from the rationale string + trust_components
 * for now (post-Iter 98 each pipeline stage will write an explicit
 * `confidence_adjustments[]` array).
 */
const WhyPanel = ({ fixtureId, marketType, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fixtureId || !marketType) return;
    setLoading(true);
    axios
      .get(`${API}/predictions/${fixtureId}/why`, {
        params: { market: marketType },
        timeout: 15000,
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [fixtureId, marketType]);

  if (!fixtureId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="why-panel-overlay"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted/40"
          aria-label="Close"
          data-testid="why-panel-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-[0.12em] uppercase bg-amber-500/15 text-amber-600 border border-amber-500/30 mb-3">
          <Sparkles className="w-3 h-3" /> Why this confidence
        </div>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 rounded bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Couldn't load explanation. Try again later.</p>
        ) : (
          <>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
              {data.match?.home_team} <span className="text-muted-foreground">vs</span>{" "}
              {data.match?.away_team}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {data.match?.league} · {data.market_type} · {data.prediction}
            </p>
            <div className="rounded-xl border border-border/40 bg-background/40 p-4 mb-4">
              <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">
                Final confidence
              </div>
              <div className="text-3xl font-display font-bold text-amber-500">
                {data.final_confidence_pct}%
              </div>
              {data.trust_score != null && (
                <div className="text-xs text-muted-foreground mt-1">
                  Trust score: {data.trust_score}/5
                </div>
              )}
            </div>

            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2">
              Adjustment trail
            </div>
            {data.adjustments && data.adjustments.length > 0 ? (
              <ul className="space-y-1.5 mb-4 max-h-64 overflow-auto" data-testid="why-adjustments">
                {data.adjustments.map((a, i) => {
                  const isDelta = typeof a.delta_pp === "number";
                  const Icon = !isDelta ? Minus : a.delta_pp >= 0 ? ArrowUp : ArrowDown;
                  const cls = !isDelta
                    ? "text-muted-foreground"
                    : a.delta_pp >= 0
                    ? "text-emerald-500"
                    : "text-rose-500";
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-sm py-1.5 px-2 rounded bg-background/40 border border-border/30"
                    >
                      <Icon className={`w-3.5 h-3.5 ${cls}`} />
                      <span className="flex-1 truncate">{a.stage}</span>
                      <span className={`font-bold tabular-nums ${cls}`}>
                        {isDelta
                          ? `${a.delta_pp >= 0 ? "+" : ""}${a.delta_pp}pp`
                          : typeof a.value === "number"
                          ? a.value
                          : String(a.value)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic mb-4">
                No granular adjustment trail recorded for this pick yet.
              </p>
            )}

            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">
              Rationale
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{data.rationale}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default WhyPanel;
